<script setup>
import Footer from '../FooterSection.vue'
import GlobalFilters from './GlobalFilters.vue'
import BookQuickActions from './BookQuickActions.vue'
import BookTableManager from './BookTableManager.vue'
import SendRequirementReminders from './SendRequirementReminders/SendRequirementReminders.vue'
import ShareUpdateView from './RequirementsCompletion/ShareUpdateView.vue'
import { useActionCenterStore } from '../../stores/ActionCenterStore'
import { RequestService } from '../../services/RequestService'
import { EmailUtilsService } from '../../services/EmailUtilsService'
import { storeToRefs } from 'pinia'
import { onMounted, onUnmounted, ref, inject, watch, nextTick } from 'vue'
import { useEventListener } from '@vueuse/core'
import { handleErrors } from '../../utils/errorHandler'
import AlertNotification from '../AlertNotification.vue'
import BcsDynamicText from '../BcsDynamicText.vue'
import megaphoneIcon from '../../../assets/images/megaphone-announcement.svg'
import { getDatabase, ref as dbRef, get as dbGet } from 'firebase/database'
import { getApp } from 'firebase/app'

const actionCenterStore = useActionCenterStore()
const { post } = RequestService()
const { getEmailTemplate } = EmailUtilsService()
const db = getDatabase(getApp())
const {  
  filteredPlanYears,
  accountOwners,
  isLoadingPlanYears,
  savedScrollPosition,
  savedDeadlinesSelection
} = storeToRefs(actionCenterStore)

// Local view state for wizard navigation
const currentActionView = ref('dashboard') // 'dashboard' | 'send-reminders' | 'share-update'
const reminderRequirements = ref([])
const remindersWasNext30DaysFilter = ref(false)

// Share update data (passed from OffCanvas)
const shareUpdateData = ref(null)
const isSendingEmail = ref(false)
const pendingScrollRestore = ref(false)
const showFeatureAnnouncement = ref(false)

// Global emitter (provided by parent/HomePage)
const emitter = inject('emitter')
const functionsBaseUrl = inject('functionsBaseUrl')
const customNavigation = inject('customNavigation')

// Save scroll position on scroll only when on dashboard view
// (sub-views like send-reminders/share-update would overwrite with their own scroll)
useEventListener(window, 'scroll', () => {
  if (currentActionView.value === 'dashboard' && !pendingScrollRestore.value) {
    savedScrollPosition.value = window.scrollY
  }
})

const restoreScrollPosition = () => {
  if (!isLoadingPlanYears.value) {
    nextTick(() => {
      window.scrollTo(0, savedScrollPosition.value)
      pendingScrollRestore.value = false
    })
  }
}

const checkFeatureAnnouncementStatus = async () => {
  try {
    const announcementRef = dbRef(db, 'TEMP/ANNOUNCEMENTS/ACTION_CENTER/enabled')
    const snapshot = await dbGet(announcementRef)
    showFeatureAnnouncement.value = snapshot.exists() && snapshot.val() === true
  } catch (error) {
    handleErrors('Error checking feature announcement status:', error)
    showFeatureAnnouncement.value = false
  }
}

const dismissFeatureAnnouncement = () => {
  showFeatureAnnouncement.value = false
}

const goToFaqs = () => {
  customNavigation.value.navigate({
    newPage: customNavigation.value.routes.frequentlyQuestions.name,
    params: { faqScrollTarget: 'faq-section-actionCenter' }
  })
}

onMounted(async () => {
  if (savedScrollPosition.value > 0) {
    restoreScrollPosition()
  }

  await checkFeatureAnnouncementStatus()

  if (emitter) {
    emitter.on('action-center:start-send-reminders', (payload = {}) => {
      reminderRequirements.value = payload.requirements || []
      remindersWasNext30DaysFilter.value = payload.wasNext30DaysFilterActive || false
      currentActionView.value = 'send-reminders'
    })

    emitter.on('action-center:start-share-update', (data) => {
      shareUpdateData.value = data || {}
      currentActionView.value = 'share-update'
    })

  }
})

function backToDashboard() {
  currentActionView.value = 'dashboard'
  shareUpdateData.value = null
  if (savedScrollPosition.value > 0) {
    pendingScrollRestore.value = true
    restoreScrollPosition()
  }
}

async function handleSendEmail(payload) {
  if (isSendingEmail.value) return
  
  isSendingEmail.value = true
  try {
    const url = `${functionsBaseUrl}/utilityGateway/api/v1/email/send`
    await post('sendShareUpdateEmail', url, {
      type: 'SHARE_UPDATE',
      data: payload,
      emailTemplate: await getEmailTemplate('SHARE_UPDATE_ACTION_ITEMS')
    })
    actionCenterStore.setShareUpdateEmailSent(true)
    backToDashboard()
  } catch (error) {
    handleErrors('Failed to send share update email:', error)
  } finally {
    isSendingEmail.value = false
  }
}

// Load historical reminders when filtered plan years change (needed for BookQuickActions and BookRequirementDeadlines)
watch(filteredPlanYears, () => {
  actionCenterStore.loadHistoricalReminders()
}, { immediate: true })

// Clear reminder notification when navigating
watch(currentActionView, (newView, oldView) => {
  if (oldView === 'dashboard' && newView === 'send-reminders') {
    actionCenterStore.setLastRemindersSummary(null)
  }
  if (newView === 'share-update' || newView === 'send-reminders') {
    nextTick(() => window.scrollTo(0, 0))
  }
})

if (savedScrollPosition.value > 0) {
  pendingScrollRestore.value = true
}

watch(isLoadingPlanYears, (isLoading, wasLoading) => {
  if (wasLoading === true && isLoading === false && pendingScrollRestore.value) {
    nextTick(() => {
      window.scrollTo(0, savedScrollPosition.value)
      pendingScrollRestore.value = false
    })
  }
})

onUnmounted(() => {
  actionCenterStore.setLastRemindersSummary(null)
  actionCenterStore.setShareUpdateEmailSent(false)
  // Clear saved selection when leaving Action Center entirely
  savedDeadlinesSelection.value = null
})
</script>

<template>
  <div class="action-center-wrapper">
    <div class="container-fluid main-container px-0 content-area">
      <template v-if="currentActionView === 'dashboard'">
        <!-- Feature Announcement Banner -->
        <div class="feature-announcement-container bcs-page-padding">
          <AlertNotification
            :showAlert="showFeatureAnnouncement"
            @update:showAlert="dismissFeatureAnnouncement"
            :showCloseButton="true"
            typeAlert="feature"
          >
            <div class="d-flex align-items-start gap-3">
              <img :src="megaphoneIcon" alt="" class="feature-announcement-icon" />
              <div class="py-3 px-2">
                <BcsDynamicText text="actionCenterFeatureAnnouncementContent" />
                <div class="d-flex align-items-center mt-2">
                  <button class="btn btn-info" @click="goToFaqs">Visit FAQs for Walkthroughs</button>
                </div>
              </div>
            </div>
          </AlertNotification>
        </div>

        <!-- Global Filters Section (includes header) -->
        <GlobalFilters />

        <template v-if="!isLoadingPlanYears && accountOwners.length > 0">
          <!-- Book Quick Actions Section (Redesigned Cards) -->
          <BookQuickActions />

          <!-- Book Details Section (View Selector + Table) -->
          <BookTableManager />
        </template>
      </template>

      <template v-else-if="currentActionView === 'send-reminders'">
        <SendRequirementReminders :requirements="reminderRequirements" :was-next-30-days-filter="remindersWasNext30DaysFilter" @back="backToDashboard" />
      </template>

      <template v-else-if="currentActionView === 'share-update'">
        <ShareUpdateView
          :share-update-data="shareUpdateData"
          @back="backToDashboard"
          @send-email="handleSendEmail"
        />
      </template>
    </div>
    <Footer />
  </div>
</template>

<style scoped lang="scss">
.action-center-wrapper {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 80px);
}

.content-area {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.feature-announcement-container {
  margin-top: 0;
  margin-bottom: 0;
}

.feature-announcement-icon {
  width: 120px;
  height: 130px;
  flex-shrink: 0;
}

</style>
